/**
 * Google Places Address Autocomplete Component
 * Uses @googlemaps/js-api-loader to provide address suggestions
 * Auto-fills street, city, state, zip when a place is selected
 */
import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps } from '../lib/googleMaps';
import { Input } from './ui/input';
import { MapPin } from 'lucide-react';

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

let googleLoaded = null;

function loadGoogle() {
  if (googleLoaded) return googleLoaded;
  if (!MAPS_KEY) return Promise.reject(new Error('No Maps key'));
  googleLoaded = loadGoogleMaps();
  return googleLoaded;
}

export default function AddressAutocomplete({
  value = '',
  onChange,
  onPlaceSelected,
  placeholder = 'Start typing an address...',
  className = '',
  'data-testid': testId = 'address-autocomplete',
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!MAPS_KEY) return;

    loadGoogle().then((google) => {
      if (!inputRef.current || autocompleteRef.current) return;

      autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
        types: ['address'],
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address', 'geometry'],
      });

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (!place?.address_components) return;

        const parsed = { street_number: '', route: '', city: '', state: '', zip: '', formatted: '' };

        place.address_components.forEach((c) => {
          const type = c.types[0];
          if (type === 'street_number') parsed.street_number = c.long_name;
          else if (type === 'route') parsed.route = c.short_name;
          else if (type === 'locality') parsed.city = c.long_name;
          else if (type === 'sublocality_level_1' && !parsed.city) parsed.city = c.long_name;
          else if (type === 'administrative_area_level_1') parsed.state = c.short_name;
          else if (type === 'postal_code') parsed.zip = c.long_name;
        });

        const street = [parsed.street_number, parsed.route].filter(Boolean).join(' ');
        parsed.formatted = place.formatted_address || street;

        const lat = place.geometry?.location?.lat();
        const lng = place.geometry?.location?.lng();

        if (onChange) onChange(street);
        if (onPlaceSelected) {
          onPlaceSelected({
            street,
            city: parsed.city,
            state: parsed.state,
            zip: parsed.zip,
            formatted_address: parsed.formatted,
            lat,
            lng,
          });
        }
      });

      setReady(true);
    }).catch(() => {
      // Maps API not available, fallback to plain input
      setReady(false);
    });
  }, []);

  return (
    <div className={`relative ${className}`}>
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange && onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="pl-9"
      />
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      {MAPS_KEY && ready && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] text-muted-foreground/50">
          Powered by Google
        </span>
      )}
    </div>
  );
}
